/**
 * EmailWidget - Client Email Completo
 *
 * Pattern: DetailPanel (pannello laterale destro, mobile = full screen)
 *
 * Quando apri un'email esistente:
 * - Visualizzazione completa dell'email (header, body, allegati)
 * - Form di risposta integrato sotto (come Outlook/Gmail)
 *
 * Quando crei una nuova email:
 * - Form completo per composizione
 */

import { useState, useRef, useEffect } from 'react';
import {
  Mail,
  Search,
  Paperclip,
  Reply,
  ReplyAll,
  Forward,
  Trash2,
  Star,
  StarOff,
  Clock,
  ArrowDownLeft,
  ArrowUpRight,
  Download,
  ExternalLink,
  Send,
  X,
  Plus,
  Bold,
  Italic,
  Underline,
  List,
  Link,
  Image,
  ChevronDown,
  ChevronUp,
  User,
  Users,
  Brain,
  Loader2,
} from 'lucide-react';
import { useHubStore, getApiEndpoints, getMessage } from '@business-assistant/shared';
import { DetailPanel, DetailSection, DetailActions } from '../common/base';
import { useTranslation } from '../../hooks/useTranslation';
import { Button, TextAreaField } from '../common/forms';

// Classification result interface
interface ClassificationResult {
  classification: 'work' | 'advertisement' | 'spam' | 'personal' | 'unknown';
  confidence: number;
  reason: string;
  suggestedCompanyName?: string;
}

// =============================================================================
// TYPES
// =============================================================================

interface EmailWidgetProps {
  isOpen?: boolean;
  emailId?: string;
  email?: Email;
  onClose: () => void;
  onSave?: (data: EmailFormData) => void;
  onSend?: (data: EmailFormData) => void;
  onDelete?: (emailId: string) => void;
  onToggleStar?: (emailId: string) => void;
}

export interface Email {
  id: string;
  from: string;
  fromName?: string;
  to: string;
  toName?: string;
  cc?: string;
  bcc?: string;
  subject: string;
  body: string;
  bodyHtml?: string;
  direction: 'in' | 'out';
  at: string;
  read?: boolean;
  starred?: boolean;
  hasAttachments?: boolean;
  attachments?: Attachment[];
  contactId?: string;
}

export interface Attachment {
  id?: string;
  name: string;
  size?: number;
  type?: string;
  url?: string;
}

export interface EmailFormData {
  from?: string;
  to: string;
  cc?: string;
  bcc?: string;
  subject: string;
  body: string;
  bodyHtml?: string;
  direction: 'in' | 'out';
  at: string;
  attachments?: Attachment[];
  replyToId?: string;
}

type ReplyMode = 'reply' | 'replyAll' | 'forward' | null;

// =============================================================================
// EMAIL VIEW WITH INTEGRATED REPLY
// =============================================================================

function EmailViewWithReply({
  email,
  onClose,
  onSend,
  onDelete,
  onToggleStar,
}: {
  email: Email;
  onClose: () => void;
  onSend?: (data: EmailFormData) => void;
  onDelete?: (emailId: string) => void;
  onToggleStar?: (emailId: string) => void;
}) {
  const { t, language } = useTranslation();
  const locale = language === 'it' ? 'it-IT' : 'en-US';
  const [replyMode, setReplyMode] = useState<ReplyMode>(null);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [replyData, setReplyData] = useState<EmailFormData>({
    from: '',
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    body: '',
    direction: 'out',
    at: new Date().toISOString(),
    attachments: [],
    replyToId: email.id,
  });
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const replyRef = useRef<HTMLDivElement>(null);

  // AI Classification state
  const [isClassifying, setIsClassifying] = useState(false);
  const [classificationResult, setClassificationResult] = useState<ClassificationResult | null>(null);
  const [classificationError, setClassificationError] = useState<string | null>(null);

  const isIncoming = email.direction === 'in';

  // Add spin animation style
  useEffect(() => {
    const styleId = 'email-widget-spin-animation';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`;
      document.head.appendChild(style);
    }
  }, []);

  // Initialize reply data when mode changes
  useEffect(() => {
    if (replyMode === 'reply') {
      setReplyData({
        ...replyData,
        to: email.from,
        subject: email.subject.startsWith('Re:') ? email.subject : `Re: ${email.subject}`,
        body: '',
      });
    } else if (replyMode === 'replyAll') {
      const allRecipients = [email.from, email.cc].filter(Boolean).join(', ');
      setReplyData({
        ...replyData,
        to: email.from,
        cc: email.cc || '',
        subject: email.subject.startsWith('Re:') ? email.subject : `Re: ${email.subject}`,
        body: '',
      });
      setShowCc(!!email.cc);
    } else if (replyMode === 'forward') {
      setReplyData({
        ...replyData,
        to: '',
        cc: '',
        subject: email.subject.startsWith('Fwd:') ? email.subject : `Fwd: ${email.subject}`,
        body: `\n\n${t('email.forwardedMessage')}\n${t('email.from')} ${email.fromName || email.from}\n${t('email.date')} ${formatDate(email.at)}\n${t('email.subject')} ${email.subject}\n${t('email.to')} ${email.toName || email.to}\n\n${email.body}`,
      });
    }
  }, [replyMode]);

  // Scroll to reply when opened
  useEffect(() => {
    if (replyMode && replyRef.current) {
      setTimeout(() => {
        replyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [replyMode]);

  const formatDate = (isoDate: string) => {
    const date = new Date(isoDate);
    return date.toLocaleDateString(locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getInitials = (name?: string, emailAddr?: string) => {
    if (name) {
      const parts = name.split(' ');
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      return name.slice(0, 2).toUpperCase();
    }
    if (emailAddr) return emailAddr.slice(0, 2).toUpperCase();
    return '??';
  };

  const handleSend = () => {
    if (onSend && replyData.to.trim()) {
      onSend({
        ...replyData,
        attachments,
        at: new Date().toISOString(),
      });
      setReplyMode(null);
      setReplyData({
        from: '',
        to: '',
        cc: '',
        bcc: '',
        subject: '',
        body: '',
        direction: 'out',
        at: new Date().toISOString(),
        attachments: [],
      });
      setAttachments([]);
    }
  };

  const handleAddAttachment = () => {
    const name = prompt('File name (e.g. document.pdf):');
    if (name?.trim()) {
      setAttachments([...attachments, { name: name.trim(), size: Math.random() * 1024 * 1024 }]);
    }
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  // AI Classification handler
  const handleClassifyEmail = async () => {
    setIsClassifying(true);
    setClassificationError(null);

    try {
      const endpoints = getApiEndpoints();
      const graphqlUrl = endpoints.graphql;

      console.log('[EmailWidget] Classifying email:', { subject: email.subject, from: email.from });

      const response = await fetch(graphqlUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: `
            mutation ClassifyEmail($input: ClassifyEmailInput!) {
              classifyEmail(input: $input) {
                success
                classification
                confidence
                reason
                suggestedCompanyName
                error
              }
            }
          `,
          variables: {
            input: {
              subject: email.subject || '',
              body: (email.body || '').substring(0, 2000),
              from: email.from,
              fromName: email.fromName || null,
            },
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      if (data.errors && data.errors.length > 0) {
        throw new Error(data.errors[0].message || 'GraphQL error');
      }

      const result = data.data?.classifyEmail;

      if (!result || !result.success) {
        throw new Error(result?.error || 'Classification failed');
      }

      console.log('[EmailWidget] Classification result:', result);

      setClassificationResult({
        classification: result.classification || 'unknown',
        confidence: result.confidence || 0,
        reason: result.reason || '',
        suggestedCompanyName: result.suggestedCompanyName || undefined,
      });
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Classification failed';
      console.error('[EmailWidget] Classification error:', errorMsg);
      setClassificationError(errorMsg);
    } finally {
      setIsClassifying(false);
    }
  };

  // Get classification badge color and label
  const getClassificationBadge = () => {
    if (!classificationResult) return null;

    const badgeStyles: Record<string, { bg: string; color: string; label: string }> = {
      work: { bg: '#dbeafe', color: '#1d4ed8', label: '💼 Lavoro' },
      personal: { bg: '#dcfce7', color: '#166534', label: '👤 Personale' },
      advertisement: { bg: '#fef3c7', color: '#92400e', label: '📢 Pubblicità' },
      spam: { bg: '#fee2e2', color: '#991b1b', label: '🚫 Spam' },
      unknown: { bg: '#f3f4f6', color: '#4b5563', label: '❓ Non classificato' },
    };

    return badgeStyles[classificationResult.classification] || badgeStyles.unknown;
  };

  return (
    <DetailPanel
      title={email.subject || t('email.noSubject')}
      subtitle={isIncoming ? t('email.emailReceived') : t('email.emailSent')}
      icon={
        <div style={{ position: 'relative' }}>
          <Mail size={20} style={{ color: '#3b82f6' }} />
          {isIncoming ? (
            <ArrowDownLeft size={10} style={{ position: 'absolute', bottom: -2, right: -2, color: '#22c55e' }} />
          ) : (
            <ArrowUpRight size={10} style={{ position: 'absolute', bottom: -2, right: -2, color: '#8b5cf6' }} />
          )}
        </div>
      }
      onClose={onClose}
      accentColor="#3b82f6"
      width="50%"
    >
      {/* ================================================================= */}
      {/* EMAIL HEADER - Outlook Style */}
      {/* ================================================================= */}
      <div style={styles.emailHeader}>
        <div style={styles.senderRow}>
          <div style={styles.avatar}>
            {getInitials(email.fromName, email.from)}
          </div>
          <div style={styles.senderInfo}>
            <div style={styles.senderName}>
              {email.fromName || email.from}
              {email.starred && <Star size={14} fill="#f59e0b" style={{ color: '#f59e0b', marginLeft: '6px' }} />}
            </div>
            <div style={styles.senderEmail}>{email.fromName ? `<${email.from}>` : ''}</div>
          </div>
          <div style={styles.dateTime}>
            <Clock size={12} style={{ marginRight: '4px' }} />
            {formatDate(email.at)}
          </div>
        </div>

        <div style={styles.recipientsRow}>
          <span style={styles.recipientLabel}>{t('email.to')}</span>
          <span style={styles.recipientValue}>{email.toName || email.to}</span>
        </div>

        {email.cc && (
          <div style={styles.recipientsRow}>
            <span style={styles.recipientLabel}>{t('email.cc')}</span>
            <span style={styles.recipientValue}>{email.cc}</span>
          </div>
        )}
      </div>

      {/* ================================================================= */}
      {/* EMAIL SUBJECT */}
      {/* ================================================================= */}
      <div style={styles.subjectBar}>
        <h2 style={styles.subject}>{email.subject || t('email.noSubject')}</h2>
      </div>

      {/* ================================================================= */}
      {/* EMAIL BODY */}
      {/* ================================================================= */}
      <div style={styles.bodyContainer}>
        {email.bodyHtml ? (
          <div
            style={styles.bodyHtml}
            dangerouslySetInnerHTML={{ __html: email.bodyHtml }}
          />
        ) : (
          <div style={styles.bodyText}>{email.body}</div>
        )}
      </div>

      {/* ================================================================= */}
      {/* ATTACHMENTS */}
      {/* ================================================================= */}
      {email.attachments && email.attachments.length > 0 && (
        <div style={styles.attachmentsSection}>
          <div style={styles.attachmentsHeader}>
            <Paperclip size={14} />
            <span>{t(email.attachments.length === 1 ? 'email.attachments' : 'email.attachments_plural', { count: email.attachments.length })}</span>
          </div>
          <div style={styles.attachmentsList}>
            {email.attachments.map((att, index) => (
              <div key={index} style={styles.attachmentChip}>
                <Paperclip size={14} style={{ color: '#6b7280' }} />
                <span style={styles.attachmentName}>{att.name}</span>
                {att.size && <span style={styles.attachmentSize}>({formatFileSize(att.size)})</span>}
                <button style={styles.attachmentDownload} title={t('email.download')}>
                  <Download size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ================================================================= */}
      {/* ACTION BUTTONS */}
      {/* ================================================================= */}
      <div style={styles.actionBar}>
        <button
          onClick={() => setReplyMode(replyMode === 'reply' ? null : 'reply')}
          style={{
            ...styles.actionBtn,
            background: replyMode === 'reply' ? '#dbeafe' : undefined,
            color: replyMode === 'reply' ? '#2563eb' : undefined,
          }}
        >
          <Reply size={16} />
          <span>{t('email.reply')}</span>
        </button>
        <button
          onClick={() => setReplyMode(replyMode === 'replyAll' ? null : 'replyAll')}
          style={{
            ...styles.actionBtn,
            background: replyMode === 'replyAll' ? '#dbeafe' : undefined,
            color: replyMode === 'replyAll' ? '#2563eb' : undefined,
          }}
        >
          <ReplyAll size={16} />
          <span>{t('email.replyAll')}</span>
        </button>
        <button
          onClick={() => setReplyMode(replyMode === 'forward' ? null : 'forward')}
          style={{
            ...styles.actionBtn,
            background: replyMode === 'forward' ? '#dbeafe' : undefined,
            color: replyMode === 'forward' ? '#2563eb' : undefined,
          }}
        >
          <Forward size={16} />
          <span>{t('email.forward')}</span>
        </button>

        {/* AI Classification Button */}
        <button
          onClick={handleClassifyEmail}
          disabled={isClassifying}
          style={{
            ...styles.actionBtn,
            background: classificationResult ? '#f0fdf4' : isClassifying ? '#f3f4f6' : undefined,
            color: classificationResult ? '#166534' : isClassifying ? '#9ca3af' : '#8b5cf6',
            cursor: isClassifying ? 'wait' : 'pointer',
          }}
          title={classificationResult ? classificationResult.reason : 'Classifica email con AI'}
        >
          {isClassifying ? (
            <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
          ) : (
            <Brain size={16} />
          )}
          <span>{isClassifying ? 'Analisi...' : 'Classifica AI'}</span>
        </button>

        {/* Classification Result Badge */}
        {classificationResult && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              borderRadius: '16px',
              fontSize: '12px',
              fontWeight: 600,
              background: getClassificationBadge()?.bg,
              color: getClassificationBadge()?.color,
            }}
            title={`${classificationResult.reason}\nConfidenza: ${Math.round(classificationResult.confidence * 100)}%${classificationResult.suggestedCompanyName ? `\nAzienda: ${classificationResult.suggestedCompanyName}` : ''}`}
          >
            {getClassificationBadge()?.label}
            <span style={{ opacity: 0.7 }}>({Math.round(classificationResult.confidence * 100)}%)</span>
          </div>
        )}

        {/* Classification Error */}
        {classificationError && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '6px 12px',
              borderRadius: '16px',
              fontSize: '12px',
              background: '#fee2e2',
              color: '#991b1b',
            }}
          >
            ⚠️ Errore
          </div>
        )}

        <div style={{ flex: 1 }} />
        {onToggleStar && (
          <button
            onClick={() => onToggleStar(email.id)}
            style={styles.actionBtn}
            title={email.starred ? t('email.removeStar') : t('email.addStar')}
          >
            {email.starred ? <Star size={16} fill="#f59e0b" style={{ color: '#f59e0b' }} /> : <StarOff size={16} />}
          </button>
        )}
        {onDelete && (
          <button
            onClick={() => onDelete(email.id)}
            style={{ ...styles.actionBtn, color: '#ef4444' }}
            title={t('common.delete')}
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>

      {/* ================================================================= */}
      {/* REPLY/FORWARD FORM (Integrated like Outlook/Gmail) */}
      {/* ================================================================= */}
      {replyMode && (
        <div ref={replyRef} style={styles.replySection}>
          <div style={styles.replyHeader}>
            <div style={styles.replyTitle}>
              {replyMode === 'reply' && <><Reply size={16} /> {t('email.reply')}</>}
              {replyMode === 'replyAll' && <><ReplyAll size={16} /> {t('email.replyAll')}</>}
              {replyMode === 'forward' && <><Forward size={16} /> {t('email.forward')}</>}
            </div>
            <button onClick={() => setReplyMode(null)} style={styles.closeReplyBtn}>
              <X size={16} />
            </button>
          </div>

          {/* Recipients */}
          <div style={styles.replyField}>
            <label style={styles.replyLabel}>{t('email.to')}</label>
            <input
              type="text"
              value={replyData.to}
              onChange={(e) => setReplyData({ ...replyData, to: e.target.value })}
              placeholder={t('email.recipientPlaceholder')}
              style={styles.replyInput}
            />
            <div style={styles.recipientActions}>
              {!showCc && (
                <button onClick={() => setShowCc(true)} style={styles.addRecipientBtn}>Cc</button>
              )}
              {!showBcc && (
                <button onClick={() => setShowBcc(true)} style={styles.addRecipientBtn}>Bcc</button>
              )}
            </div>
          </div>

          {showCc && (
            <div style={styles.replyField}>
              <label style={styles.replyLabel}>{t('email.cc')}</label>
              <input
                type="text"
                value={replyData.cc}
                onChange={(e) => setReplyData({ ...replyData, cc: e.target.value })}
                placeholder="cc@email.com"
                style={styles.replyInput}
              />
              <button onClick={() => { setShowCc(false); setReplyData({ ...replyData, cc: '' }); }} style={styles.removeFieldBtn}>
                <X size={14} />
              </button>
            </div>
          )}

          {showBcc && (
            <div style={styles.replyField}>
              <label style={styles.replyLabel}>{t('email.bcc')}</label>
              <input
                type="text"
                value={replyData.bcc}
                onChange={(e) => setReplyData({ ...replyData, bcc: e.target.value })}
                placeholder="bcc@email.com"
                style={styles.replyInput}
              />
              <button onClick={() => { setShowBcc(false); setReplyData({ ...replyData, bcc: '' }); }} style={styles.removeFieldBtn}>
                <X size={14} />
              </button>
            </div>
          )}

          {/* Subject */}
          <div style={styles.replyField}>
            <label style={styles.replyLabel}>{t('email.subject')}</label>
            <input
              type="text"
              value={replyData.subject}
              onChange={(e) => setReplyData({ ...replyData, subject: e.target.value })}
              style={styles.replyInput}
            />
          </div>

          {/* Editor Toolbar */}
          <div style={styles.editorToolbar}>
            <button style={styles.toolbarBtn} title={t('email.bold')}><Bold size={16} /></button>
            <button style={styles.toolbarBtn} title={t('email.italic')}><Italic size={16} /></button>
            <button style={styles.toolbarBtn} title={t('email.underline')}><Underline size={16} /></button>
            <div style={styles.toolbarDivider} />
            <button style={styles.toolbarBtn} title={t('email.list')}><List size={16} /></button>
            <button style={styles.toolbarBtn} title={t('email.link')}><Link size={16} /></button>
            <button style={styles.toolbarBtn} title={t('email.image')}><Image size={16} /></button>
          </div>

          {/* Body */}
          <textarea
            value={replyData.body}
            onChange={(e) => setReplyData({ ...replyData, body: e.target.value })}
            placeholder={t('email.writeYourMessage')}
            style={styles.replyTextarea}
            rows={8}
          />

          {/* Attachments */}
          {attachments.length > 0 && (
            <div style={styles.replyAttachments}>
              {attachments.map((att, index) => (
                <div key={index} style={styles.replyAttachmentChip}>
                  <Paperclip size={12} />
                  <span>{att.name}</span>
                  <button onClick={() => handleRemoveAttachment(index)} style={styles.removeAttachmentBtn}>
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div style={styles.replyActions}>
            <Button variant="secondary" icon={Paperclip} onClick={handleAddAttachment}>
              {t('email.attach')}
            </Button>
            <div style={{ flex: 1 }} />
            <Button variant="secondary" onClick={() => setReplyMode(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              icon={Send}
              onClick={handleSend}
              disabled={!replyData.to.trim()}
            >
              {t('common.send')}
            </Button>
          </div>
        </div>
      )}

      {/* Footer */}
      <DetailActions>
        <Button variant="secondary" onClick={onClose}>
          {t('common.close')}
        </Button>
      </DetailActions>
    </DetailPanel>
  );
}

// =============================================================================
// COMPOSE NEW EMAIL
// =============================================================================

function ComposeEmail({
  onClose,
  onSend,
}: {
  onClose: () => void;
  onSend?: (data: EmailFormData) => void;
}) {
  const { t } = useTranslation();
  const { contacts } = useHubStore();
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [formData, setFormData] = useState<EmailFormData>({
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    body: '',
    direction: 'out',
    at: new Date().toISOString(),
  });
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const [contactSearch, setContactSearch] = useState('');

  const filteredContacts = contacts
    .filter(
      (contact) =>
        contact.displayName.toLowerCase().includes(contactSearch.toLowerCase()) ||
        (contact.handles.email && contact.handles.email.toLowerCase().includes(contactSearch.toLowerCase()))
    )
    .slice(0, 5);

  const handleSelectContact = (contact: (typeof contacts)[0]) => {
    setFormData({ ...formData, to: contact.handles.email || '' });
    setContactSearch(contact.handles.email || contact.displayName);
    setShowContactDropdown(false);
  };

  const handleSend = () => {
    if (onSend && formData.to.trim()) {
      onSend({ ...formData, attachments });
    }
  };

  const handleAddAttachment = () => {
    const name = prompt('File name (e.g. document.pdf):');
    if (name?.trim()) {
      setAttachments([...attachments, { name: name.trim(), size: Math.random() * 1024 * 1024 }]);
    }
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  return (
    <DetailPanel
      title={t('email.newEmail')}
      subtitle={t('email.composeMessage')}
      icon={<Mail size={20} style={{ color: '#3b82f6' }} />}
      onClose={onClose}
      accentColor="#3b82f6"
      width="50%"
    >
      {/* Recipients */}
      <div style={styles.composeField}>
        <label style={styles.composeLabel}>{t('email.to')}</label>
        <div style={{ flex: 1, position: 'relative' }}>
          <input
            type="text"
            value={contactSearch || formData.to}
            onChange={(e) => {
              setContactSearch(e.target.value);
              setFormData({ ...formData, to: e.target.value });
              setShowContactDropdown(true);
            }}
            onFocus={() => setShowContactDropdown(true)}
            onBlur={() => setTimeout(() => setShowContactDropdown(false), 200)}
            placeholder={t('email.recipientPlaceholder')}
            style={styles.composeInput}
          />
          {showContactDropdown && filteredContacts.length > 0 && (
            <div style={styles.dropdown}>
              {filteredContacts.map((contact) => (
                <button
                  key={contact.id}
                  type="button"
                  onClick={() => handleSelectContact(contact)}
                  style={styles.dropdownItem}
                >
                  <div style={styles.dropdownAvatar}>{contact.displayName[0]?.toUpperCase()}</div>
                  <div>
                    <div style={{ fontWeight: 500 }}>{contact.displayName}</div>
                    {contact.handles.email && (
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>{contact.handles.email}</div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        <div style={styles.recipientActions}>
          {!showCc && <button onClick={() => setShowCc(true)} style={styles.addRecipientBtn}>Cc</button>}
          {!showBcc && <button onClick={() => setShowBcc(true)} style={styles.addRecipientBtn}>Bcc</button>}
        </div>
      </div>

      {showCc && (
        <div style={styles.composeField}>
          <label style={styles.composeLabel}>{t('email.cc')}</label>
          <input
            type="text"
            value={formData.cc}
            onChange={(e) => setFormData({ ...formData, cc: e.target.value })}
            placeholder="cc@email.com"
            style={styles.composeInput}
          />
          <button onClick={() => { setShowCc(false); setFormData({ ...formData, cc: '' }); }} style={styles.removeFieldBtn}>
            <X size={14} />
          </button>
        </div>
      )}

      {showBcc && (
        <div style={styles.composeField}>
          <label style={styles.composeLabel}>{t('email.bcc')}</label>
          <input
            type="text"
            value={formData.bcc}
            onChange={(e) => setFormData({ ...formData, bcc: e.target.value })}
            placeholder="bcc@email.com"
            style={styles.composeInput}
          />
          <button onClick={() => { setShowBcc(false); setFormData({ ...formData, bcc: '' }); }} style={styles.removeFieldBtn}>
            <X size={14} />
          </button>
        </div>
      )}

      {/* Subject */}
      <div style={styles.composeField}>
        <label style={styles.composeLabel}>{t('email.subject')}</label>
        <input
          type="text"
          value={formData.subject}
          onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
          placeholder={t('email.emailSubject')}
          style={styles.composeInput}
        />
      </div>

      {/* Editor Toolbar */}
      <div style={styles.editorToolbar}>
        <button style={styles.toolbarBtn} title={t('email.bold')}><Bold size={16} /></button>
        <button style={styles.toolbarBtn} title={t('email.italic')}><Italic size={16} /></button>
        <button style={styles.toolbarBtn} title={t('email.underline')}><Underline size={16} /></button>
        <div style={styles.toolbarDivider} />
        <button style={styles.toolbarBtn} title={t('email.list')}><List size={16} /></button>
        <button style={styles.toolbarBtn} title={t('email.link')}><Link size={16} /></button>
        <button style={styles.toolbarBtn} title={t('email.image')}><Image size={16} /></button>
      </div>

      {/* Body */}
      <textarea
        value={formData.body}
        onChange={(e) => setFormData({ ...formData, body: e.target.value })}
        placeholder={t('email.writeYourMessage')}
        style={styles.composeTextarea}
        rows={12}
      />

      {/* Attachments */}
      {attachments.length > 0 && (
        <div style={styles.replyAttachments}>
          {attachments.map((att, index) => (
            <div key={index} style={styles.replyAttachmentChip}>
              <Paperclip size={12} />
              <span>{att.name}</span>
              <button onClick={() => handleRemoveAttachment(index)} style={styles.removeAttachmentBtn}>
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <DetailActions>
        <Button variant="secondary" icon={Paperclip} onClick={handleAddAttachment}>
          {t('email.attach')}
        </Button>
        <div style={{ flex: 1 }} />
        <Button variant="secondary" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="primary"
          icon={Send}
          onClick={handleSend}
          disabled={!formData.to.trim()}
        >
          {t('common.send')}
        </Button>
      </DetailActions>
    </DetailPanel>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function EmailWidget({
  isOpen = true,
  emailId,
  email,
  onClose,
  onSave,
  onSend,
  onDelete,
  onToggleStar,
}: EmailWidgetProps) {
  const { t } = useTranslation();
  const { getConversation, getConversationMessages, getContact } = useHubStore();

  // Debug logging
  console.log('[EmailWidget] Props received:', { emailId, email: email ? 'provided' : 'not provided', isOpen });

  // State for async loading of IMAP emails
  const [imapEmail, setImapEmail] = useState<Email | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // For IMAP emails (eml-*), ALWAYS fetch from IMAP to get full body
  // The email prop may only contain snippet from list view
  const isImapEmail = emailId?.startsWith('eml-');

  // Fetch IMAP email - always fetch for eml-* IDs to get full body
  useEffect(() => {
    console.log('[EmailWidget] useEffect triggered:', { emailId, hasEmail: !!email, isImapEmail });

    // For non-IMAP emails, use provided email prop
    if (!isImapEmail) {
      console.log('[EmailWidget] Not an IMAP email, using prop or hub store');
      return;
    }

    if (!emailId) {
      return;
    }

    // IMAP email - always fetch to get full body (prop may only have snippet)
    if (isImapEmail) {
      const uid = parseInt(emailId.replace('eml-', ''), 10);
      if (!isNaN(uid)) {
        setIsLoading(true);
        setLoadError(null);

        getMessage(uid, 'INBOX')
          .then((imapMsg) => {
            console.log('[EmailWidget] IMAP message loaded:', imapMsg);
            console.log('[EmailWidget] Body length:', imapMsg.body?.length || 0);
            // Convert IMAP message to Email format
            const emailData: Email = {
              id: emailId,
              from: imapMsg.from?.address || '',
              fromName: imapMsg.from?.name,
              to: imapMsg.to[0]?.address || '',
              toName: imapMsg.to[0]?.name,
              cc: imapMsg.cc?.map(c => c.address).join(', '),
              subject: imapMsg.subject || '(No subject)',
              body: imapMsg.body || '',
              bodyHtml: imapMsg.bodyHtml || undefined,
              direction: 'in',
              at: imapMsg.date,
              read: imapMsg.flags.includes('\\Seen'),
              hasAttachments: imapMsg.attachments.length > 0,
              attachments: imapMsg.attachments.map(att => ({
                name: att.filename,
                size: att.size,
                type: att.contentType,
              })),
            };
            console.log('[EmailWidget] Email data created:', emailData);
            setImapEmail(emailData);
          })
          .catch((err) => {
            console.error('[EmailWidget] Failed to load IMAP email:', err);
            setLoadError(err.message || 'Failed to load email');
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    }
  }, [emailId, isImapEmail]);

  // Determine what email data to use
  // For IMAP emails, prefer imapEmail (full body) over email prop (may be snippet)
  let emailData = isImapEmail ? imapEmail : (email || imapEmail);

  // If no email data yet, try to load from hub store (for conversations)
  if (!emailData && emailId && !isImapEmail) {
    const conversation = getConversation(emailId);
    if (conversation && conversation.channel === 'email') {
      const messages = getConversationMessages(emailId);
      const contact = conversation.contactId ? getContact(conversation.contactId) : null;
      const lastMessage = messages[messages.length - 1];

      emailData = {
        id: conversation.id,
        from: lastMessage?.direction === 'in'
          ? (contact?.handles?.email || contact?.displayName || 'unknown@email.com')
          : 'me@company.com',
        fromName: lastMessage?.direction === 'in' ? contact?.displayName : 'You',
        to: lastMessage?.direction === 'out'
          ? (contact?.handles?.email || contact?.displayName || 'recipient@email.com')
          : 'me@company.com',
        toName: lastMessage?.direction === 'out' ? contact?.displayName : 'You',
        subject: conversation.title,
        body: messages.map(m => m.text).join('\n\n---\n\n'),
        direction: (lastMessage?.direction || 'in') as 'in' | 'out',
        at: conversation.lastAt,
        read: !conversation.unread,
        attachments: [],
      };
    }
  }

  if (!isOpen) return null;

  // Show loading state
  if (isLoading) {
    return (
      <DetailPanel
        title={t('common.loading')}
        subtitle=""
        icon={<Mail size={20} />}
        onClose={onClose}
        accentColor="#3b82f6"
        width="50%"
      >
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem' }}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      </DetailPanel>
    );
  }

  // Show error state
  if (loadError) {
    return (
      <DetailPanel
        title={t('common.error')}
        subtitle=""
        icon={<Mail size={20} />}
        onClose={onClose}
        accentColor="#ef4444"
        width="50%"
      >
        <div style={{ padding: '2rem', textAlign: 'center', color: '#ef4444' }}>
          <p>{loadError}</p>
        </div>
      </DetailPanel>
    );
  }

  // Se abbiamo un'email esistente, mostra VIEW con risposta integrata
  if (emailData) {
    return (
      <EmailViewWithReply
        email={emailData}
        onClose={onClose}
        onSend={onSend || onSave}
        onDelete={onDelete}
        onToggleStar={onToggleStar}
      />
    );
  }

  // Altrimenti, mostra form di composizione nuova email
  return <ComposeEmail onClose={onClose} onSend={onSend || onSave} />;
}

// =============================================================================
// STYLES
// =============================================================================

const styles: Record<string, React.CSSProperties> = {
  // Email Header
  emailHeader: {
    background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '16px',
  },
  senderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
  },
  avatar: {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '15px',
    fontWeight: 600,
    flexShrink: 0,
  },
  senderInfo: {
    flex: 1,
  },
  senderName: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#1f2937',
    display: 'flex',
    alignItems: 'center',
  },
  senderEmail: {
    fontSize: '13px',
    color: '#6b7280',
  },
  dateTime: {
    fontSize: '12px',
    color: '#6b7280',
    display: 'flex',
    alignItems: 'center',
  },
  recipientsRow: {
    display: 'flex',
    gap: '8px',
    fontSize: '13px',
    paddingLeft: '56px',
    marginBottom: '4px',
  },
  recipientLabel: {
    color: '#6b7280',
    minWidth: '24px',
  },
  recipientValue: {
    color: '#374151',
  },

  // Subject
  subjectBar: {
    marginBottom: '16px',
  },
  subject: {
    margin: 0,
    fontSize: '20px',
    fontWeight: 600,
    color: '#1f2937',
    lineHeight: 1.3,
  },

  // Body
  bodyContainer: {
    background: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '10px',
    padding: '20px',
    marginBottom: '16px',
    minHeight: '150px',
  },
  bodyText: {
    fontSize: '14px',
    lineHeight: 1.7,
    color: '#374151',
    whiteSpace: 'pre-wrap',
  },
  bodyHtml: {
    fontSize: '14px',
    lineHeight: 1.6,
    color: '#374151',
  },

  // Attachments
  attachmentsSection: {
    marginBottom: '16px',
  },
  attachmentsHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '10px',
  },
  attachmentsList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  attachmentChip: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    background: '#f3f4f6',
    borderRadius: '8px',
    fontSize: '13px',
  },
  attachmentName: {
    color: '#374151',
    fontWeight: 500,
  },
  attachmentSize: {
    color: '#9ca3af',
    fontSize: '12px',
  },
  attachmentDownload: {
    padding: '4px',
    border: 'none',
    borderRadius: '4px',
    background: 'transparent',
    cursor: 'pointer',
    color: '#6b7280',
    display: 'flex',
  },

  // Action Bar
  actionBar: {
    display: 'flex',
    gap: '8px',
    padding: '12px 0',
    borderTop: '1px solid #e5e7eb',
    borderBottom: '1px solid #e5e7eb',
    marginBottom: '16px',
  },
  actionBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 14px',
    border: 'none',
    borderRadius: '8px',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 500,
    color: '#4b5563',
    transition: 'all 0.15s',
  },

  // Reply Section
  replySection: {
    background: '#f8fafc',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '16px',
  },
  replyHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
  },
  replyTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
  },
  closeReplyBtn: {
    padding: '6px',
    border: 'none',
    borderRadius: '6px',
    background: 'transparent',
    cursor: 'pointer',
    color: '#6b7280',
    display: 'flex',
  },
  replyField: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
  },
  replyLabel: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#6b7280',
    minWidth: '60px',
  },
  replyInput: {
    flex: 1,
    padding: '10px 12px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    background: 'white',
  },
  recipientActions: {
    display: 'flex',
    gap: '4px',
  },
  addRecipientBtn: {
    padding: '6px 10px',
    border: 'none',
    borderRadius: '6px',
    background: '#e5e7eb',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 500,
    color: '#4b5563',
  },
  removeFieldBtn: {
    padding: '6px',
    border: 'none',
    borderRadius: '6px',
    background: 'transparent',
    cursor: 'pointer',
    color: '#9ca3af',
    display: 'flex',
  },

  // Editor Toolbar
  editorToolbar: {
    display: 'flex',
    gap: '2px',
    padding: '8px',
    background: 'white',
    border: '1px solid #e5e7eb',
    borderBottom: 'none',
    borderRadius: '8px 8px 0 0',
    marginBottom: '-1px',
  },
  toolbarBtn: {
    padding: '8px',
    border: 'none',
    borderRadius: '6px',
    background: 'transparent',
    cursor: 'pointer',
    color: '#6b7280',
    display: 'flex',
  },
  toolbarDivider: {
    width: '1px',
    background: '#e5e7eb',
    margin: '0 4px',
  },

  // Textarea
  replyTextarea: {
    width: '100%',
    padding: '14px',
    border: '1px solid #e5e7eb',
    borderRadius: '0 0 8px 8px',
    fontSize: '14px',
    lineHeight: 1.6,
    resize: 'vertical',
    outline: 'none',
    fontFamily: 'inherit',
    minHeight: '150px',
    background: 'white',
  },

  // Reply Attachments
  replyAttachments: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '12px',
  },
  replyAttachmentChip: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 10px',
    background: '#e5e7eb',
    borderRadius: '6px',
    fontSize: '12px',
    color: '#374151',
  },
  removeAttachmentBtn: {
    padding: '2px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    color: '#6b7280',
    display: 'flex',
  },

  // Reply Actions
  replyActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginTop: '16px',
  },

  // Compose form
  composeField: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
  },
  composeLabel: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#6b7280',
    minWidth: '60px',
  },
  composeInput: {
    flex: 1,
    padding: '10px 12px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
  },
  composeTextarea: {
    width: '100%',
    padding: '14px',
    border: '1px solid #e5e7eb',
    borderRadius: '0 0 8px 8px',
    fontSize: '14px',
    lineHeight: 1.6,
    resize: 'vertical',
    outline: 'none',
    fontFamily: 'inherit',
    minHeight: '250px',
  },

  // Dropdown
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    background: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    zIndex: 10,
    marginTop: '4px',
    maxHeight: '200px',
    overflowY: 'auto',
  },
  dropdownItem: {
    width: '100%',
    padding: '10px 12px',
    border: 'none',
    borderBottom: '1px solid #f3f4f6',
    background: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    textAlign: 'left',
  },
  dropdownAvatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: '#3b82f6',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: 600,
  },
};
